import { useEffect, useState } from 'react';
import {
  BreadcrumbHeading,
  DownloadIcon,
  Plus,
  Prompt,
  Tab,
  TabList,
  TabsProvider,
  TabContent,
  SnackbarStatus,
  Upload,
  useSnackbar,
  VenueIcon,
} from '@weareopenr/component-library';
import { saveAs } from 'file-saver';
import EmptyState from 'src/components/EmptyState';
import VenuesModal from 'src/components/VenuesModal';
import { CreateVenueModal } from 'src/components/CreateVenueModal/CreateVenueModal';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { downloadVenuesTemplateCSV } from 'src/api/downloadVenuesTemplateCSV/downloadVenuesTemplateCSV';
import { error } from 'src/mocks/data';
import styles from './Venues.module.scss';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { deleteVenue } from 'src/api/deleteVenue/deleteVenue';
import VenuesTab from 'src/components/VenuesTab';
import VenueCategoriesTab from 'src/components/VenueCategoriesTab';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

const Venues = () => {
  const { data: venues = [], isLoading, refetch } = useVenues();
  const { featureFlags } = useAuthWrapper();
  const { showSnackbar } = useSnackbar();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isCreateVenueModalVisible, setIsCreateVenueModalVisible] = useState<boolean>(false);
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState<boolean>(false);
  const [venueToRemoveId, setVenueToRemoveId] = useState<string | undefined>();
  const { trackEvent, captureException } = useUserTracking();

  const venueToRemoveName = venues?.find((venue) => venue.venue_id === venueToRemoveId)?.name;
  const removePromptTitle = `Are you sure you want to delete "${venueToRemoveName}" venue?`;
  const removeVenuePromptSubTitle = 'You will lose all configuration and no longer be able to publish to this venue.';

  const handleModalToggle = (): void => {
    setIsModalVisible((prev) => !prev);
  };

  const handlePromptToggle = (): void => {
    setIsRemovePromptVisible((prev) => !prev);
  };

  const handleCreateVenueModalToggle = (): void => {
    setIsCreateVenueModalVisible((prev) => !prev);
  };

  useEffect(() => {
    document.title = 'Venues';
    trackEvent('page_viewed', { page: 'venues' });
  }, []);

  const handleDownload = () => {
    downloadVenuesTemplateCSV()
      .then((response) => {
        saveAs(response, `template.csv`);
      })
      .catch(() => {
        showSnackbar({ status: SnackbarStatus.ERROR, message: error });
      });
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteVenue(venueToRemoveId);
      await refetch();
      trackEvent('venue_deleted', { venueId: venueToRemoveId, venueName: venueToRemoveName });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted ${venueToRemoveName} venue!`,
      });
    } catch (e) {
      captureException(e, {
        tags: {
          action: 'delete_venue',
          feature: 'venue_management',
        },
      });
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'We were unable to delete, please try again!' });
    } finally {
      handlePromptToggle();
    }
  };

  const handleOpenDeletePrompt = (id: string) => {
    setVenueToRemoveId(id);
    handlePromptToggle();
  };

  if (isLoading) return <SkeletonContainer />;

  return (
    <TabsProvider defaultTab="venues">
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={handlePromptToggle}
        open={isRemovePromptVisible}
        buttonHandler={handleDelete}
        buttonText="Delete"
        backButton={true}
        size="small"
      >
        {removeVenuePromptSubTitle}
      </Prompt>
      <div className={styles['page-container']}>
        <VenuesModal closeModal={handleModalToggle} open={isModalVisible} />
        <CreateVenueModal closeModal={handleCreateVenueModalToggle} open={isCreateVenueModalVisible} />
        <div className={styles.headerContainer}>
          <BreadcrumbHeading
            breadcrumbs={[{ label: 'Venues', path: '/venues' }]}
            optionMenuItems={[
              { name: 'Create venue', onOptionClick: handleCreateVenueModalToggle, id: 'create-venue', icon: Plus },
              { name: 'Import venues', onOptionClick: handleModalToggle, id: 'import-venues', icon: Upload },
              {
                name: 'Download template',
                onOptionClick: handleDownload,
                id: 'download-template',
                icon: DownloadIcon,
              },
            ]}
          />
        </div>
        {venues.length > 0 ? (
          <>
            <TabList>
              <Tab id="venues" tabText="Venues" />
              {featureFlags.includes('venue_categories') && <Tab id="venue-categories" tabText="Categories" />}
            </TabList>
            <TabContent id="venues">
              <div className={styles['table-container']}>
                <VenuesTab venues={venues} handleOpenDeletePrompt={handleOpenDeletePrompt} />
              </div>
            </TabContent>

            {featureFlags.includes('venue_categories') && (
              <TabContent id="venue-categories">
                <div className={styles['table-container']}>
                  <VenueCategoriesTab venues={venues} />
                </div>
              </TabContent>
            )}
          </>
        ) : (
          <div className={styles.emptyStateContainer}>
            <EmptyState
              emptySectionText="Add your first venue"
              buttonText="Create venue"
              Icon={VenueIcon}
              onClick={handleCreateVenueModalToggle}
              type="venues"
            />
          </div>
        )}
      </div>
    </TabsProvider>
  );
};

export default Venues;
